import * as THREE from 'three';
import { Scorestreaks } from './Scorestreaks.js';
import { ScorestreakHUD } from '../ui/Scorestreaks.js';

/**
 * Match flow and cross-system wiring.
 *
 * The feature modules deliberately know nothing about each other — they only speak
 * the event vocabulary in ARCHITECTURE.md. This is the one place allowed to know
 * about all of them, so every "when X happens, tell Y" rule lives here rather than
 * being smeared across the codebase.
 */

const TUNING = {
  killTarget: 30,
  respawnDelay: 3.2,
  // Score feedback
  xpKill: 100,
  xpHeadshot: 150,
  streakWindow: 4.0,
  /** Kill volume. Below this, or this far outside navBounds, you are dead. */
  killPlaneY: -20,
  boundsSlack: 12,
};

export class GameDirector {
  constructor(ctx) {
    this.ctx = ctx;
    this.state = 'playing';
    this.kills = 0;
    this.deaths = 0;
    this.xp = 0;
    this.streak = 0;
    this._lastKillTime = -99;
    this._respawnTimer = 0;

    // Scorestreaks are two halves of one feature: the system that owns the ladder
    // and everything it puts on the map, and the HUD that reports it. They talk to
    // each other only through the `streak:*` events, so either can be driven alone.
    this.scorestreaks = new Scorestreaks(ctx);
    this.streakHud = new ScorestreakHUD(ctx);

    this._wire();
    ctx.engine.registerUpdate((dt) => this.update(dt), 'ui');
  }

  _wire() {
    const { events, ctx } = { events: this.ctx.events, ctx: this.ctx };

    /* --- kills: score, streaks, killfeed --- */
    events.on('actor:death', ({ headshot, mine }) => {
      if (!mine) return;
      this.kills++;
      const now = ctx.engine.elapsed;
      this.streak = now - this._lastKillTime < TUNING.streakWindow ? this.streak + 1 : 1;
      this._lastKillTime = now;
      const gained = headshot ? TUNING.xpHeadshot : TUNING.xpKill;
      this.xp += gained;

      // Streak progress is score, not kills — that is the whole reason a headshot
      // and a multikill are worth more than a body shot on the ladder as well as
      // on the scoreboard. This is the only place that knows what a kill was worth.
      this.scorestreaks.award(gained, { headshot, streak: this.streak });

      events.emit('ui:score', { kills: this.kills, xp: this.xp, streak: this.streak });
      this._checkRulesWin();
      events.emit('audio:play', { id: headshot ? 'ui_headshot' : 'ui_kill', volume: 0.6 });

      if (this.streak >= 3) {
        events.emit('ui:banner', {
          key: this.streak >= 5 ? 'banner.rampage' : 'banner.doubleKill',
          tone: 'good',
        });
      }
      /**
       * The thirty-kill target is Skirmish's win condition, not the game's. A mode
       * that keeps its own score owns the ending, and leaving this unguarded means
       * a Team Deathmatch stops dead the moment one player personally reaches
       * thirty — regardless of what either team's score actually is.
       */
      if (!ctx.match?.rules?.ownsVictory && this.kills >= TUNING.killTarget) this._win();
    });

    /* --- the mode may end the match on its own clock, not only on a kill --- */
    events.on('match:score', () => this._checkRulesWin());

    /* --- damage feedback into the post-processing stack --- */
    events.on('player:health', ({ health, max }) => {
      const hurt = 1 - health / max;
      // Only the bottom half of the health range drives the effect, so a scratch
      // doesn't tint the whole screen.
      ctx.postfx?.setDamageIntensity?.(Math.max(0, (hurt - 0.45) / 0.55));
    });

    events.on('player:damage', ({ amount }) => {
      ctx.postfx?.flash?.(Math.min(amount / 260, 0.12), 0.12);
      events.emit('audio:play', { id: 'hurt', volume: Math.min(0.3 + amount / 60, 1) });
    });

    /* --- ADS drives peripheral blur --- */
    events.on('player:state', ({ ads }) => {
      ctx.postfx?.setAdsBlur?.(ads ? 1 : 0);
    });

    /* --- explosions shake the camera and deafen the player briefly --- */
    events.on('fx:explosion', ({ position, power }) => {
      const d = ctx.player ? ctx.player.position.distanceTo(position) : 99;
      if (d < 14) {
        ctx.postfx?.flash?.(Math.min((1 - d / 14) * 0.5, 0.5), 0.2);
        ctx.audio?.suppressHearing?.(Math.min((1 - d / 14) * (power ?? 1), 1), 2.2);
      }
    });

    /* --- death and respawn --- */
    events.on('player:death', () => {
      this.deaths++;
      this.state = 'dead';
      this._respawnTimer = TUNING.respawnDelay;
      events.emit('audio:play', { id: 'death', volume: 0.9 });
    });

    /* --- unsuppressed fire close to the ears --- */
    events.on('weapon:fire', () => {
      ctx.audio?.duckWorld?.(0.12);
    });
  }

  update(dt) {
    // We run on the 'ui' phase because match flow must survive a dead player, but
    // 'ui' is also the one phase the engine keeps ticking while paused. Without
    // this guard the respawn timer ran behind an open pause menu and then emitted
    // `game:state playing`, un-pausing the game underneath the player.
    if (this.ctx.engine.paused) return;

    // The mode's own clock. Round timers and capture ticks live behind this one
    // call, so a mode never has to register an update of its own.
    const rules = this.ctx.match?.rules;
    if (rules?.tick) {
      rules.tick(dt, this.ctx);
      this._checkRulesWin();
    }

    /**
     * Whether the player is alive is a fact; `this.state` is a cache of it, and
     * the cache can now go stale. A round-based mode revives the player itself at
     * the start of a round without going through `_respawn`, which left this
     * reading 'dead' for a living player — and with it the out-of-bounds check
     * below, so the kill plane quietly stopped existing after round one and
     * walking off the map dropped you forever again.
     */
    if (this.state === 'dead' && this.ctx.player && !this.ctx.player.dead) {
      this.state = 'playing';
      this._respawnTimer = 0;
    }

    if (this.state === 'dead') {
      /**
       * The mode gets a veto. Round-based modes answer no for the rest of the
       * round and put the player back themselves when the next one starts — the
       * three-second timer below is a Deathmatch rule that had never had to say
       * so out loud, because it was the only rule there was.
       */
      if (rules?.allowRespawn?.(this.ctx) === false) return;
      this._respawnTimer -= dt;
      if (this._respawnTimer <= 0) this._respawn();
    } else {
      this._checkOutOfBounds();
    }
    if (this.ctx.engine.elapsed - this._lastKillTime > TUNING.streakWindow) this.streak = 0;
  }

  /**
   * Kill volume. The map has no perimeter wall, so running off the edge used to
   * drop the player forever — measured at 827 m below the world, at terminal
   * velocity, still alive and still "playing", recoverable only via the menu.
   */
  _checkOutOfBounds() {
    const p = this.ctx.player;
    if (!p || p.dead) return;
    const b = this.ctx.level?.navBounds;
    const belowFloor = p.position.y < TUNING.killPlaneY;
    const outside =
      b && (p.position.x < b.min.x - TUNING.boundsSlack ||
            p.position.x > b.max.x + TUNING.boundsSlack ||
            p.position.z < b.min.z - TUNING.boundsSlack ||
            p.position.z > b.max.z + TUNING.boundsSlack);
    if (belowFloor || outside) p.damage(999, null);
  }

  /**
   * The mode gets first refusal on ending the match. Skirmish declines and the
   * thirty-kill target below stands, which is why this changes nothing today — but
   * a mode with a score limit, a round timer or a planted bomb answers here instead
   * of this file growing a branch per mode.
   */
  _checkRulesWin() {
    const verdict = this.ctx.match?.rules?.checkWin?.(this.ctx.match);
    if (verdict) this._win(verdict);
  }

  _respawn() {
    const { ctx } = this;
    /**
     * The mode picks first. Skirmish returns null, leaving the search below — the
     * spawn furthest from any living enemy, so the player isn't spawn-killed — in
     * charge. Team modes answer with their own half of the map.
     */
    const ruled = ctx.match?.rules?.pickSpawn?.(ctx.match.get(ctx.player), ctx);
    if (ruled) {
      ctx.player.respawn(ruled.clone ? ruled.clone() : ruled);
      this.state = 'playing';
      ctx.postfx?.setDamageIntensity?.(0);
      ctx.events.emit('game:state', { state: 'playing' });
      return;
    }
    // Pick the spawn furthest from any living enemy so the player isn't spawn-killed.
    const spawns = ctx.level?.enemySpawns?.length ? [ctx.level.playerSpawn, ...ctx.level.enemySpawns] : [ctx.level?.playerSpawn ?? new THREE.Vector3(0, 2, 0)];
    const enemies = ctx.ai?.enemies ?? [];
    let best = spawns[0];
    let bestScore = -Infinity;
    for (const s of spawns) {
      if (!s) continue;
      let nearest = Infinity;
      for (const e of enemies) {
        if (e.dead) continue;
        nearest = Math.min(nearest, e.position.distanceTo(s));
      }
      if (nearest > bestScore) {
        bestScore = nearest;
        best = s;
      }
    }
    ctx.player.respawn(best.clone());
    this.state = 'playing';
    ctx.postfx?.setDamageIntensity?.(0);
    ctx.events.emit('game:state', { state: 'playing' });
  }

  _win(verdict = null) {
    if (this.state === 'won') return;
    this.state = 'won';
    this.ctx.events.emit('game:state', { state: 'won', ...(verdict ?? {}) });
    this.ctx.events.emit('ui:banner', {
      key: verdict?.key ?? 'banner.missionComplete',
      tone: verdict && verdict.win === false ? 'bad' : 'good',
    });
  }
}
